import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Quote, Loader2, Send } from "lucide-react";
import { api } from "../../lib/site";
import { Reveal, Overline } from "./ui";

const Stars = ({ value, onChange, size = 16 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={onChange ? "button" : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "transition-transform hover:scale-110" : "pointer-events-none"}
        aria-label={`${n} star`}
        data-testid={onChange ? `star-${n}` : undefined}
      >
        <Star size={size} className={n <= value ? "fill-volt text-volt" : "text-white/25"} />
      </button>
    ))}
  </div>
);

export default function Reviews() {
  const [enabled, setEnabled] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    Promise.all([api.get("/settings"), api.get("/reviews")])
      .then(([s, r]) => {
        setEnabled(s.data.reviews_enabled);
        setReviews(r.data);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks! Your review was submitted for approval.");
      setForm({ name: "", rating: 5, text: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready || !enabled) return null;

  return (
    <section className="relative bg-ink-surface px-5 py-24 md:px-10 md:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="mb-14 max-w-2xl">
          <Overline>Rider Stories</Overline>
          <h2 className="mt-4 font-heading text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl md:text-5xl">
            Trusted by local explorers
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {reviews.length === 0 ? (
              <p className="text-white/50">Be the first to share your experience.</p>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {reviews.map((r, i) => (
                  <Reveal key={r.id} delay={(i % 2) * 0.08}>
                    <div className="flex h-full flex-col border border-white/10 bg-ink p-6" data-testid={`review-${r.id}`}>
                      <Quote className="text-volt/40" size={22} />
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-white/80">"{r.text}"</p>
                      <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                        <span className="font-heading text-sm font-bold uppercase tracking-wide text-white">{r.name}</span>
                        <Stars value={r.rating} />
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="border border-white/10 bg-ink p-6 md:p-7" data-testid="review-form">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white">Leave a review</h3>
              <p className="mt-1 text-xs text-white/50">Shown after a quick check by our team.</p>
              <div className="mt-5 space-y-4">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="review-name"
                  className="w-full border border-white/15 bg-ink-surface px-4 py-3 text-sm text-white outline-none focus:border-volt"
                />
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-white/50">Rating</span>
                  <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={20} />
                </div>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  rows={4}
                  placeholder="How was your rental experience?"
                  data-testid="review-text"
                  className="w-full border border-white/15 bg-ink-surface px-4 py-3 text-sm text-white outline-none focus:border-volt"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="review-submit"
                  className="flex w-full items-center justify-center gap-2 bg-volt py-3.5 text-sm font-bold uppercase tracking-wider text-ink transition-colors hover:bg-volt-hover disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <><Send size={15} /> Submit Review</>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
